import { Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';
import Layout from '../components/Layout';
import { Button } from '../components/ui/button';
import { ArrowRight, Calendar, MapPin, Gift, Star, Sparkles, Sun, Cloud, CloudRain, Snowflake, Wind, Droplets, ThermometerSun, Navigation, ClipboardCheck, FileText, CalendarCheck, LayoutDashboard, Sunset, Camera, Volume2, VolumeX, Clock, Key, Copy, Check, MessageCircle, Send, Utensils, Waves, Compass, X, ChevronDown } from 'lucide-react';
import { useState, useEffect, useRef } from 'react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Paestum timezone offset (CET/CEST)
const getPaestumTime = () => {
  const now = new Date();
  // Convert to Italy time
  const italyTime = new Date(now.toLocaleString('en-US', { timeZone: 'Europe/Rome' }));
  return italyTime;
};

// Get time of day theme
const getTimeTheme = () => {
  const hour = getPaestumTime().getHours();
  if (hour >= 5 && hour < 8) return 'dawn';      // Alba
  if (hour >= 8 && hour < 17) return 'day';      // Giorno
  if (hour >= 17 && hour < 20) return 'sunset';  // Tramonto (ora d'oro!)
  return 'night';                                 // Notte
};

// Calculate golden hour countdown
const getGoldenHourInfo = () => {
  const now = getPaestumTime();
  const hour = now.getHours();
  const minutes = now.getMinutes();
  
  // Golden hour is typically 17:00-20:00 for photos
  const goldenStart = 17;
  const goldenEnd = 20;
  
  if (hour >= goldenStart && hour < goldenEnd) {
    return { isGoldenHour: true, message: 'ORA D\'ORO ATTIVA!', emoji: '✨' };
  }
  
  // Calculate time until golden hour
  let hoursUntil, minutesUntil;
  if (hour < goldenStart) {
    hoursUntil = goldenStart - hour - 1;
    minutesUntil = 60 - minutes;
    if (minutesUntil === 60) { hoursUntil++; minutesUntil = 0; }
  } else {
    // After golden hour, show next day
    hoursUntil = (24 - hour) + goldenStart - 1;
    minutesUntil = 60 - minutes;
    if (minutesUntil === 60) { hoursUntil++; minutesUntil = 0; }
  }
  
  return { 
    isGoldenHour: false, 
    hoursUntil, 
    minutesUntil,
    message: `Ora d'oro tra ${hoursUntil}h ${minutesUntil}m`,
    emoji: '📸'
  };
};

// Timeline data for Paestum history
const paestumTimeline = [
  { year: '600 a.C.', title: 'Fondazione', titleEn: 'Foundation', desc: 'I Greci fondano Poseidonia', descEn: 'Greeks found Poseidonia' },
  { year: '450 a.C.', title: 'Età dell\'Oro', titleEn: 'Golden Age', desc: 'Costruzione dei templi maestosi', descEn: 'Construction of majestic temples' },
  { year: '273 a.C.', title: 'Era Romana', titleEn: 'Roman Era', desc: 'Diventa colonia romana Paestum', descEn: 'Becomes Roman colony Paestum' },
  { year: '1752', title: 'Riscoperta', titleEn: 'Rediscovery', desc: 'Riportata alla luce dopo secoli', descEn: 'Brought back to light after centuries' },
  { year: '1998', title: 'UNESCO', titleEn: 'UNESCO', desc: 'Patrimonio dell\'Umanità', descEn: 'World Heritage Site' },
];

export default function HomePage() {
  const { isAuthenticated, token, user } = useAuth();
  const { language, t } = useLanguage();
  const [weather, setWeather] = useState(null);
  const [itineraries, setItineraries] = useState([]);
  const [events, setEvents] = useState([]);
  const [rewards, setRewards] = useState([]);
  const [loadingWeather, setLoadingWeather] = useState(true);
  const [hasActiveCheckin, setHasActiveCheckin] = useState(false);
  const [activeCheckinData, setActiveCheckinData] = useState(null);
  const [codiceIngresso, setCodiceIngresso] = useState(null);
  const [codeCopied, setCodeCopied] = useState(false);
  
  // New states for unique features
  const [timeTheme, setTimeTheme] = useState(getTimeTheme());
  const [goldenHour, setGoldenHour] = useState(getGoldenHourInfo());
  const [soundEnabled, setSoundEnabled] = useState(false);
  const [mousePos, setMousePos] = useState({ x: 0, y: 0 });
  const audioRef = useRef(null);
  const heroRef = useRef(null);
  
  // Oggi a Paestum
  const [oggiPaestum, setOggiPaestum] = useState(null);
  const [loadingOggi, setLoadingOggi] = useState(true);
  const [showAiChat, setShowAiChat] = useState(false);
  const [aiMessages, setAiMessages] = useState([]);
  const [aiInput, setAiInput] = useState('');
  const [aiLoading, setAiLoading] = useState(false);
  const [selectedPlace, setSelectedPlace] = useState(null); // Per modale dettagli

  // Helper to get translated content
  const getLocalizedText = (item, field) => {
    if (language === 'en' && item[`${field}_en`]) {
      return item[`${field}_en`];
    }
    return item[field] || item[`${field}_it`] || '';
  };

  useEffect(() => {
    fetchData();
  }, []);

  useEffect(() => {
    if (token) {
      checkActiveCheckin();
    }
  }, [token]);

  const checkActiveCheckin = async () => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/checkin/active`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setHasActiveCheckin(!!data.has_active_checkin);
        setActiveCheckinData(data.checkin || data.booking || null);
        setCodiceIngresso(data.codice_ingresso);
      }
    } catch (error) {
      console.error('Error checking checkin:', error);
    }
  };

  const fetchData = async () => {
    try {
      // Fetch "Oggi a Paestum" - aggregated data
      const oggiRes = await fetch(`${BACKEND_URL}/api/oggi-a-paestum`);
      if (oggiRes.ok) {
        const oggiData = await oggiRes.json();
        setOggiPaestum(oggiData);
        // Set weather from oggi-a-paestum if available
        if (oggiData.meteo) {
          setWeather({
            temperatura: oggiData.meteo.temp,
            umidita: oggiData.meteo.humidity,
            vento: oggiData.meteo.wind_speed,
            condizione: oggiData.meteo.is_rainy ? 'rainy' : oggiData.meteo.is_beach_day ? 'sunny' : 'cloudy',
            descrizione: oggiData.meteo.description,
            localita: 'Paestum'
          });
        }
      }
      setLoadingOggi(false);
      
      // Fetch weather and itineraries (fallback)
      const weatherRes = await fetch(`${BACKEND_URL}/api/itineraries/suggested`);
      if (weatherRes.ok) {
        const data = await weatherRes.json();
        if (!oggiPaestum?.meteo) {
          setWeather(data.meteo);
        }
        setItineraries(data.itinerari_suggeriti || []);
      } else if (!oggiPaestum?.meteo) {
        // Fallback: meteo demo se l'API non funziona
        setWeather({
          temperatura: 22,
          umidita: 65,
          vento: 12,
          condizione: 'sunny',
          descrizione: 'Soleggiato',
          localita: 'Paestum'
        });
      }
      
      // Fetch events
      const eventsRes = await fetch(`${BACKEND_URL}/api/events`);
      if (eventsRes.ok) {
        const eventsData = await eventsRes.json();
        setEvents(eventsData.slice(0, 3)); // Show only 3 events
      }
      
      // Fetch loyalty rewards
      const rewardsRes = await fetch(`${BACKEND_URL}/api/loyalty/rewards`);
      if (rewardsRes.ok) {
        const rewardsData = await rewardsRes.json();
        setRewards(rewardsData.slice(0, 4)); // Show first 4 rewards
      }
    } catch (error) {
      console.error('Error fetching data:', error);
      setLoadingOggi(false);
      // Fallback meteo in caso di errore
      setWeather({
        temperatura: 22,
        umidita: 65,
        vento: 12,
        condizione: 'sunny',
        descrizione: 'Soleggiato',
        localita: 'Paestum'
      });
    } finally {
      setLoadingWeather(false);
    }
  };

  // AI Chat function
  const sendAiMessage = async () => {
    if (!aiInput.trim()) return;
    
    const userMessage = aiInput.trim();
    setAiInput('');
    setAiMessages(prev => [...prev, { role: 'user', content: userMessage }]);
    setAiLoading(true);
    
    try {
      const response = await fetch(`${BACKEND_URL}/api/ai/concierge`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token && { Authorization: `Bearer ${token}` })
        },
        body: JSON.stringify({ 
          message: userMessage,
          context: {
            meteo: oggiPaestum?.meteo,
            eventi_oggi: oggiPaestum?.eventi_oggi?.length || 0,
            suggerimento: oggiPaestum?.suggerimento_ai?.testo
          }
        })
      });
      
      if (response.ok) {
        const data = await response.json();
        setAiMessages(prev => [...prev, { role: 'assistant', content: data.response || data.message }]);
      } else {
        // Risposta di fallback
        setAiMessages(prev => [...prev, { 
          role: 'assistant', 
          content: `Oggi a Paestum ti consiglio: ${oggiPaestum?.suggerimento_ai?.testo || 'Esplora i templi e goditi il mare!'}` 
        }]);
      }
    } catch (error) {
      setAiMessages(prev => [...prev, { 
        role: 'assistant', 
        content: 'Mi dispiace, non riesco a rispondere al momento. Prova a esplorare le sezioni qui sotto!' 
      }]);
    } finally {
      setAiLoading(false);
    }
  };

  // Update time theme and golden hour every minute
  useEffect(() => {
    const interval = setInterval(() => {
      setTimeTheme(getTimeTheme());
      setGoldenHour(getGoldenHourInfo());
    }, 60000);
    return () => clearInterval(interval);
  }, []);

  // Parallax effect on mouse move
  useEffect(() => {
    const handleMouseMove = (e) => {
      if (heroRef.current) {
        const { clientX, clientY } = e;
        const { innerWidth, innerHeight } = window;
        const x = (clientX / innerWidth - 0.5) * 20;
        const y = (clientY / innerHeight - 0.5) * 20;
        setMousePos({ x, y });
      }
    };
    window.addEventListener('mousemove', handleMouseMove);
    return () => window.removeEventListener('mousemove', handleMouseMove);
  }, []);

  // Get theme-based overlay colors
  const getThemeOverlay = () => {
    switch (timeTheme) {
      case 'dawn': return 'from-purple-900/40 via-orange-500/20 to-yellow-500/30';
      case 'day': return 'from-blue-900/30 via-transparent to-blue-900/40';
      case 'sunset': return 'from-orange-600/40 via-red-500/20 to-purple-900/50';
      case 'night': return 'from-indigo-950/60 via-blue-900/40 to-indigo-950/70';
      default: return 'from-black/30 via-transparent to-black/50';
    }
  };

  // Toggle ambient sound
  const toggleSound = () => {
    if (audioRef.current) {
      if (soundEnabled) {
        audioRef.current.pause();
      } else {
        audioRef.current.play();
      }
      setSoundEnabled(!soundEnabled);
    }
  };

  const getWeatherIcon = (condition) => {
    switch (condition) {
      case 'sunny': return <Sun className="w-10 h-10 text-yellow-500" />;
      case 'cloudy': return <Cloud className="w-10 h-10 text-gray-400" />;
      case 'rainy': return <CloudRain className="w-10 h-10 text-blue-500" />;
      case 'cold': return <Snowflake className="w-10 h-10 text-blue-300" />;
      default: return <Sun className="w-10 h-10 text-yellow-500" />;
    }
  };

  const getCategoryIcon = (categoria) => {
    switch (categoria) {
      case 'spiaggia': return '🏖️';
      case 'cultura': return '🏛️';
      case 'natura': return '🌿';
      case 'gastronomia': return '🍷';
      case 'relax': return '💆';
      default: return '📍';
    }
  };

  return (
    <Layout>
      {/* Ambient Audio (Mediterranean/relaxing music) */}
      <audio 
        ref={audioRef} 
        loop 
        src="https://cdn.pixabay.com/download/audio/2022/05/27/audio_1808fbf07a.mp3"
        preload="none"
      />

      {/* Sound Toggle Button */}
      <button
        onClick={toggleSound}
        className="fixed bottom-24 right-4 md:bottom-8 md:right-8 z-50 w-12 h-12 rounded-full bg-white/90 backdrop-blur-sm shadow-lg flex items-center justify-center transition-all hover:scale-110 hover:bg-[#C5A059] hover:text-white group"
        title={soundEnabled ? 'Disattiva suoni' : 'Attiva suoni ambiente'}
        data-testid="sound-toggle"
      >
        {soundEnabled ? (
          <Volume2 className="w-5 h-5 text-[#C5A059] group-hover:text-white" />
        ) : (
          <VolumeX className="w-5 h-5 text-gray-400 group-hover:text-white" />
        )}
      </button>

      {/* Golden Hour Indicator */}
      <div 
        className={`fixed top-20 right-4 md:top-24 md:right-8 z-40 px-4 py-2 rounded-full backdrop-blur-md shadow-lg flex items-center gap-2 transition-all ${
          goldenHour.isGoldenHour 
            ? 'bg-gradient-to-r from-amber-500/90 to-orange-500/90 text-white animate-pulse-glow' 
            : 'bg-white/80 text-gray-700'
        }`}
        data-testid="golden-hour-indicator"
      >
        <Camera className={`w-4 h-4 ${goldenHour.isGoldenHour ? 'text-white' : 'text-[#C5A059]'}`} />
        <span className="text-xs font-medium">
          {goldenHour.emoji} {goldenHour.isGoldenHour ? (language === 'en' ? 'GOLDEN HOUR!' : 'ORA D\'ORO!') : goldenHour.message}
        </span>
      </div>

      {/* Hero Section - Elegante con Carattere */}
      <section ref={heroRef} className="relative min-h-screen flex items-center justify-center overflow-hidden">
        {/* Background Image */}
        <div 
          className="absolute inset-0 bg-cover bg-center"
          style={{
            backgroundImage: `url('https://images.unsplash.com/photo-1504644708628-9c1dd99f497f?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NzV8MHwxfHNlYXJjaHwyfHxQYWVzdHVtJTIwdGVtcGxlJTIwZ3JlZWslMjBydWlucyUyMGdvbGRlbiUyMGhvdXJ8ZW58MHx8fHwxNzY2MzU1NTAwfDA&ixlib=rb-4.1.0&q=85')`
          }}
        />
        {/* Elegant Dark Overlay */}
        <div className="absolute inset-0 bg-gradient-to-b from-black/60 via-black/40 to-black/70" />
        
        <div className="relative z-10 text-center text-white px-6 max-w-4xl mx-auto">
          {/* Logo */}
          <img 
            src="https://customer-assets.emergentagent.com/job_9e63d10a-d9aa-4e49-9076-0c5c1ecf4133/artifacts/vr9w9ixp_P%C3%86STUM__5_-removebg-preview.png" 
            alt="La Maisonette Paestum" 
            className="h-24 md:h-36 mx-auto mb-6 brightness-0 invert"
          />
          
          {/* Decorative Line */}
          <div className="w-16 h-0.5 bg-[#C5A059] mx-auto mb-6"></div>
          
          {/* Tagline */}
          <h1 className="font-cormorant text-2xl md:text-3xl lg:text-4xl italic text-white mb-8">
            {language === 'en' 
              ? '20 steps from the Ancient City of Paestum'
              : 'A 20 passi dall\'Antica Città di Paestum'}
          </h1>
          
          {/* Rating elegante */}
          <div className="inline-flex items-center gap-3 bg-white/10 backdrop-blur-sm px-6 py-3 rounded-full mb-8">
            <Star className="w-5 h-5 text-[#C5A059] fill-[#C5A059]" />
            <span className="text-white font-semibold text-lg">9.3</span>
            <span className="text-white/70 text-sm">Booking.com</span>
            <span className="w-1 h-1 bg-white/40 rounded-full"></span>
            <span className="text-white/70 text-sm">90+ recensioni</span>
          </div>
          
          {/* Servizi in linea */}
          <div className="flex flex-wrap justify-center gap-6 mb-10 text-white/80 text-sm">
            <span className="flex items-center gap-2">
              <Key className="w-4 h-4 text-[#C5A059]" />
              Self Check-in
            </span>
            <span className="flex items-center gap-2">
              <Sparkles className="w-4 h-4 text-[#C5A059]" />
              Wi-Fi Veloce
            </span>
            <span className="flex items-center gap-2">
              <MessageCircle className="w-4 h-4 text-[#C5A059]" />
              Supporto 24/7
            </span>
          </div>
          
          {/* CTA Buttons */}
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/booking">
              <Button className="bg-[#C5A059] hover:bg-[#B08D45] text-white px-10 py-6 text-base rounded-lg shadow-lg">
                {t('home.bookNow')}
                <CalendarCheck className="ml-2 w-5 h-5" />
              </Button>
            </Link>
            {isAuthenticated && hasActiveCheckin ? (
              <Link to="/dashboard">
                <Button variant="outline" className="border-white/60 text-white hover:bg-white/10 px-10 py-6 text-base rounded-lg">
                  {language === 'en' ? 'Your Dashboard' : 'La Tua Dashboard'}
                </Button>
              </Link>
            ) : (
              <Link to={isAuthenticated ? "/checkin" : "/login"}>
                <Button variant="outline" className="border-white/60 text-white hover:bg-white/10 px-10 py-6 text-base rounded-lg">
                  {language === 'en' ? 'Online Check-in' : 'Check-in Online'}
                </Button>
              </Link>
            )}
          </div>
        </div>

        {/* Scroll indicator */}
        <div className="absolute bottom-8 left-1/2 -translate-x-1/2">
          <ChevronDown className="w-6 h-6 text-white/50 animate-bounce" />
        </div>
      </section>

      {/* ===================== CODICE INGRESSO - BANNER PROMINENTE ===================== */}
      {isAuthenticated && hasActiveCheckin && codiceIngresso && (
        <section className="bg-gradient-to-r from-[#1B4332] via-[#2D5A45] to-[#1B4332] py-6 md:py-8 relative overflow-hidden">
          {/* Background pattern */}
          <div className="absolute inset-0 opacity-10">
            <div className="absolute inset-0" style={{
              backgroundImage: 'url("data:image/svg+xml,%3Csvg width=\'60\' height=\'60\' viewBox=\'0 0 60 60\' xmlns=\'http://www.w3.org/2000/svg\'%3E%3Cg fill=\'none\' fill-rule=\'evenodd\'%3E%3Cg fill=\'%23ffffff\' fill-opacity=\'0.4\'%3E%3Cpath d=\'M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z\'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")'
            }} />
          </div>
          
          <div className="max-w-4xl mx-auto px-4 relative z-10">
            {/* Unit Name Header */}
            {activeCheckinData?.unit_name && (
              <div className="text-center mb-4">
                <p className="text-[#C5A059] font-cinzel text-xl md:text-2xl tracking-wide">
                  {activeCheckinData.unit_name}
                </p>
              </div>
            )}
            
            <div className="flex flex-col md:flex-row items-center justify-between gap-6">
              {/* Left side - Icon and Text */}
              <div className="flex items-center gap-4 text-white">
                <div className="w-16 h-16 md:w-20 md:h-20 rounded-2xl bg-[#C5A059] flex items-center justify-center shadow-xl">
                  <Key className="w-8 h-8 md:w-10 md:h-10 text-white" />
                </div>
                <div>
                  <p className="text-[#C5A059] font-cinzel text-xs md:text-sm tracking-widest uppercase mb-1">
                    {language === 'en' ? 'Entry Code' : 'Codice di Ingresso'}
                  </p>
                  <p className="text-sm md:text-base text-gray-200">
                    {language === 'en' 
                      ? 'Use this code to access your accommodation'
                      : 'Usa questo codice per accedere alla tua struttura'}
                  </p>
                </div>
              </div>
              
              {/* Right side - Code Display */}
              <div className="flex items-center gap-3">
                <div 
                  className="bg-white/10 backdrop-blur-sm border-2 border-[#C5A059] rounded-2xl px-6 md:px-10 py-4 md:py-5 cursor-pointer hover:bg-white/20 transition-all group"
                  onClick={() => {
                    navigator.clipboard.writeText(codiceIngresso);
                    setCodeCopied(true);
                    setTimeout(() => setCodeCopied(false), 2000);
                  }}
                  data-testid="entry-code-display"
                >
                  <div className="flex items-center gap-4">
                    <span className="text-4xl md:text-5xl font-bold text-white tracking-[0.3em] font-mono">
                      {codiceIngresso}
                    </span>
                    <div className="p-2 rounded-lg bg-[#C5A059]/20 group-hover:bg-[#C5A059]/40 transition-colors">
                      {codeCopied ? (
                        <Check className="w-6 h-6 text-green-400" />
                      ) : (
                        <Copy className="w-6 h-6 text-[#C5A059]" />
                      )}
                    </div>
                  </div>
                  <p className="text-xs text-center text-gray-300 mt-2">
                    {codeCopied 
                      ? (language === 'en' ? '✓ Copied!' : '✓ Copiato!') 
                      : (language === 'en' ? 'Tap to copy' : 'Tocca per copiare')}
                  </p>
                </div>
              </div>
            </div>
            
            {/* Date Info */}
            {activeCheckinData && (activeCheckinData.data_arrivo || activeCheckinData.check_in) && (
              <div className="mt-4 pt-4 border-t border-white/20 flex flex-wrap justify-center gap-4 md:gap-8 text-sm text-gray-300">
                <span className="flex items-center gap-2">
                  <Calendar className="w-4 h-4 text-[#C5A059]" />
                  {new Date(activeCheckinData.data_arrivo || activeCheckinData.check_in).toLocaleDateString('it-IT')} - {new Date(activeCheckinData.data_partenza || activeCheckinData.check_out).toLocaleDateString('it-IT')}
                </span>
              </div>
            )}
          </div>
        </section>
      )}

      {/* Weather Section with Tour Suggestions */}
      {weather && (
        <section className="py-6 md:py-8 glass-dark">
          <div className="max-w-7xl mx-auto px-4">
            {/* Main Weather Display */}
            <div className="flex flex-col md:flex-row items-center justify-between gap-4 md:gap-6">
              <div className="flex items-center gap-3 md:gap-4">
                <div className="w-14 h-14 md:w-16 md:h-16 flex items-center justify-center animate-float">
                  {getWeatherIcon(weather.condizione)}
                </div>
                <div className="text-white">
                  <p className="font-cinzel text-xs text-[#C5A059] uppercase tracking-wider">Paestum Ora</p>
                  <p className="font-cinzel text-3xl md:text-4xl">{weather.temperatura}°C</p>
                </div>
              </div>
              
              <div className="flex items-center gap-4 md:gap-6 text-white text-sm">
                <div className="flex items-center gap-2">
                  <Droplets className="w-4 h-4 text-blue-400" />
                  <span>{weather.umidita}%</span>
                </div>
                <div className="flex items-center gap-2">
                  <Wind className="w-4 h-4 text-gray-400" />
                  <span>{weather.vento} km/h</span>
                </div>
              </div>
            </div>
            
            {/* Link to Itineraries */}
            <div className="mt-4 pt-4 border-t border-white/10">
              <Link 
                to="/itineraries" 
                className="flex items-center gap-2 text-[#C5A059] hover:text-white transition-colors text-sm font-medium"
              >
                <Compass className="w-4 h-4" />
                Scopri cosa fare oggi
              </Link>
            </div>
          </div>
        </section>
      )}

      {/* ==================== COSA FARE OGGI - Sezione Principale ==================== */}
      <section className="py-12 px-4 bg-[#F9F9F7]">
        <div className="max-w-7xl mx-auto">
          {/* Header con Meteo */}
          <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-8">
            <div>
              <p className="font-cormorant text-[#C5A059] tracking-[0.2em] uppercase text-sm mb-2">
                Cosa Fare Oggi
              </p>
              <h2 className="font-cinzel text-2xl md:text-3xl text-[#1A202C]">
                {weather 
                  ? `${weather.descrizione}, ${weather.temperatura}°C`
                  : 'Scopri gli itinerari consigliati'
                }
              </h2>
            </div>
            {weather && (
              <div className="flex items-center gap-3 bg-white rounded-xl px-4 py-3 shadow-soft">
                <div className="p-2 bg-[#C5A059]/10 rounded-full">
                  {weather.condizione === 'sunny' && <Sun className="w-6 h-6 text-yellow-500" />}
                  {weather.condizione === 'cloudy' && <Cloud className="w-6 h-6 text-gray-500" />}
                  {weather.condizione === 'rainy' && <CloudRain className="w-6 h-6 text-blue-500" />}
                  {weather.condizione === 'cold' && <Snowflake className="w-6 h-6 text-cyan-500" />}
                </div>
                <div>
                  <p className="font-cinzel text-xl text-[#1A202C]">{weather.temperatura}°C</p>
                  <p className="text-xs text-[#4A5568]">{weather.localita || 'Paestum'}</p>
                </div>
              </div>
            )}
          </div>

          {/* Contenuto principale: 1 itinerario grande + 2 piccoli */}
          {itineraries.length > 0 ? (
            <div className="grid md:grid-cols-3 gap-6">
              {/* Itinerario Principale */}
              {itineraries[0] && (() => {
                const mainIt = itineraries.find(it => weather?.condizione === it.condizione_meteo) || itineraries[0];
                const defaultImages = {
                  'spiaggia': 'https://images.unsplash.com/photo-1507525428034-b723cf961d3e?w=600&h=400&fit=crop',
                  'cultura': 'https://images.unsplash.com/photo-1552832230-c0197dd311b5?w=600&h=400&fit=crop',
                  'natura': 'https://images.unsplash.com/photo-1441974231531-c6227db76b6e?w=600&h=400&fit=crop',
                  'gastronomia': 'https://images.unsplash.com/photo-1414235077428-338989a2e8c0?w=600&h=400&fit=crop',
                };
                return (
                  <Link
                    to={`/itineraries/${mainIt.id}`}
                    className="md:col-span-2 group relative rounded-2xl overflow-hidden h-64 md:h-80"
                  >
                    <img 
                      src={mainIt.immagine_url || defaultImages[mainIt.categoria] || defaultImages.cultura}
                      alt={mainIt.nome}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                      onError={(e) => e.target.src = defaultImages.cultura}
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-transparent" />
                    <div className="absolute bottom-0 left-0 right-0 p-6 text-white">
                      <div className="flex items-center gap-2 mb-2">
                        <span className="bg-[#C5A059] px-3 py-1 rounded-full text-xs font-medium flex items-center gap-1">
                          <Sparkles className="w-3 h-3" />
                          Consigliato oggi
                        </span>
                        <span className="bg-white/20 backdrop-blur-sm px-3 py-1 rounded-full text-xs">
                          {mainIt.categoria?.charAt(0).toUpperCase() + mainIt.categoria?.slice(1)}
                        </span>
                      </div>
                      <h3 className="font-cinzel text-2xl mb-2">{mainIt.nome}</h3>
                      <p className="text-sm text-gray-200 line-clamp-2 mb-3">{mainIt.descrizione}</p>
                      <div className="flex items-center gap-4 text-xs text-gray-300">
                        <span className="flex items-center gap-1"><MapPin className="w-3 h-3" /> {mainIt.luogo}</span>
                        {mainIt.durata && <span className="flex items-center gap-1"><Clock className="w-3 h-3" /> {mainIt.durata}</span>}
                      </div>
                    </div>
                  </Link>
                );
              })()}

              {/* Due itinerari alternativi compatti */}
              <div className="flex flex-col gap-4">
                {itineraries.filter((it, i) => i > 0).slice(0, 2).map((it) => {
                  const defaultImages = {
                    'spiaggia': 'https://images.unsplash.com/photo-1507525428034-b723cf961d3e?w=300&h=200&fit=crop',
                    'cultura': 'https://images.unsplash.com/photo-1552832230-c0197dd311b5?w=300&h=200&fit=crop',
                    'natura': 'https://images.unsplash.com/photo-1441974231531-c6227db76b6e?w=300&h=200&fit=crop',
                    'gastronomia': 'https://images.unsplash.com/photo-1414235077428-338989a2e8c0?w=300&h=200&fit=crop',
                  };
                  return (
                    <Link
                      key={it.id}
                      to={`/itineraries/${it.id}`}
                      className="group flex gap-4 bg-white rounded-xl p-3 shadow-soft hover:shadow-lg transition-all"
                    >
                      <img 
                        src={it.immagine_url || defaultImages[it.categoria] || defaultImages.cultura}
                        alt={it.nome}
                        className="w-24 h-24 object-cover rounded-lg flex-shrink-0"
                        onError={(e) => e.target.src = defaultImages.cultura}
                      />
                      <div className="flex-1 min-w-0">
                        <span className="text-xs text-[#C5A059] font-medium">
                          {it.categoria?.charAt(0).toUpperCase() + it.categoria?.slice(1)}
                        </span>
                        <h4 className="font-cinzel text-[#1A202C] truncate group-hover:text-[#C5A059] transition-colors">
                          {it.nome}
                        </h4>
                        <p className="text-xs text-[#4A5568] flex items-center gap-1 mt-1">
                          <MapPin className="w-3 h-3" /> {it.luogo}
                        </p>
                      </div>
                    </Link>
                  );
                })}
                
                {/* Pulsante "Esplora tutti gli itinerari" */}
                <Link
                  to="/itineraries"
                  className="flex items-center justify-center gap-2 bg-[#1A202C] text-white py-4 rounded-xl hover:bg-[#2D3748] transition-colors font-medium"
                >
                  <Compass className="w-5 h-5" />
                  Esplora tutti gli itinerari
                </Link>
              </div>
            </div>
          ) : (
            <div className="text-center py-8 bg-white rounded-xl shadow-soft">
              <Compass className="w-12 h-12 mx-auto mb-4 text-[#C5A059]/50" />
              <p className="text-[#4A5568] mb-4">Scopri le esperienze consigliate dalla Maisonette.</p>
              <Link to="/itineraries">
                <Button className="bg-[#C5A059] hover:bg-[#B08D45]">
                  Esplora gli itinerari
                </Button>
              </Link>
            </div>
          )}
        </div>
      </section>

      {/* ==================== OGGI A PAESTUM ==================== */}
      <section className="py-12 px-4 bg-gradient-to-b from-[#F8F6F3] to-white">
        <div className="max-w-7xl mx-auto">
          {/* Header */}
          <div className="text-center mb-10">
            <p className="font-cormorant text-[#C5A059] tracking-widest uppercase text-sm mb-2">
              {language === 'en' ? 'Your Daily Guide' : 'La Tua Guida Quotidiana'}
            </p>
            <h2 className="font-cinzel text-3xl md:text-4xl text-[#1B4332] mb-4">
              {language === 'en' ? 'Today in Paestum' : 'Oggi a Paestum'}
            </h2>
            <div className="w-24 h-1 bg-gradient-to-r from-transparent via-[#C5A059] to-transparent mx-auto" />
          </div>

          {loadingOggi ? (
            <div className="flex justify-center py-12">
              <div className="animate-spin w-8 h-8 border-4 border-[#1B4332] border-t-transparent rounded-full" />
            </div>
          ) : (
            <>
              {/* AI Suggestion Card */}
              {oggiPaestum?.suggerimento_ai && (
                <div className="mb-8 bg-gradient-to-r from-[#1B4332] to-[#2D5A45] rounded-2xl p-6 text-white relative overflow-hidden">
                  <div className="absolute top-0 right-0 w-32 h-32 bg-[#C5A059]/10 rounded-full -translate-y-1/2 translate-x-1/2" />
                  <div className="relative z-10 flex flex-col md:flex-row items-center justify-between gap-4">
                    <div className="flex items-center gap-4">
                      <div className="w-14 h-14 bg-[#C5A059]/20 rounded-xl flex items-center justify-center">
                        <Sparkles className="w-7 h-7 text-[#C5A059]" />
                      </div>
                      <div>
                        <h3 className="font-cinzel text-lg">{oggiPaestum.suggerimento_ai.titolo}</h3>
                        <p className="text-gray-200 text-sm">{oggiPaestum.suggerimento_ai.testo}</p>
                      </div>
                    </div>
                    <div className="flex gap-3">
                      <Link 
                        to={oggiPaestum.suggerimento_ai.link || '/structures'}
                        className="bg-[#C5A059] hover:bg-[#D4AF6A] text-[#1B4332] px-5 py-2 rounded-lg font-medium transition-colors"
                      >
                        {oggiPaestum.suggerimento_ai.azione}
                      </Link>
                      <button 
                        onClick={() => setShowAiChat(true)}
                        className="bg-white/10 hover:bg-white/20 px-4 py-2 rounded-lg transition-colors flex items-center gap-2"
                      >
                        <MessageCircle className="w-4 h-4" />
                        {language === 'en' ? 'Ask AI' : 'Chiedi all\'AI'}
                      </button>
                    </div>
                  </div>
                </div>
              )}

              {/* Grid principale */}
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                
                {/* EVENTI OGGI */}
                <div className="bg-white rounded-2xl shadow-lg p-6 border border-gray-100">
                  <div className="flex items-center gap-3 mb-4">
                    <div className="w-10 h-10 bg-purple-100 rounded-lg flex items-center justify-center">
                      <Calendar className="w-5 h-5 text-purple-600" />
                    </div>
                    <h3 className="font-cinzel text-lg text-[#1B4332]">
                      {language === 'en' ? 'Events' : 'Eventi'}
                    </h3>
                  </div>
                  {oggiPaestum?.eventi_oggi?.length > 0 ? (
                    <div className="space-y-3">
                      {oggiPaestum.eventi_oggi.slice(0, 3).map((evento, idx) => (
                        <button 
                          key={idx}
                          onClick={() => setSelectedPlace({ ...evento, tipo: 'evento' })}
                          className="w-full p-3 bg-gray-50 rounded-lg hover:bg-purple-50 hover:border-purple-200 border border-transparent transition-colors text-left"
                        >
                          <div className="font-medium text-sm text-[#1B4332]">{evento.nome}</div>
                          <div className="text-xs text-gray-500 flex items-center gap-1 mt-1">
                            <MapPin className="w-3 h-3" /> {evento.luogo || 'Paestum'}
                            {evento.data && <span className="ml-2">• {new Date(evento.data).toLocaleDateString('it-IT', { day: 'numeric', month: 'short' })}</span>}
                          </div>
                        </button>
                      ))}
                    </div>
                  ) : (
                    <p className="text-gray-400 text-sm">{language === 'en' ? 'No events today' : 'Nessun evento oggi'}</p>
                  )}
                  <Link to="/events" className="mt-4 text-[#C5A059] text-sm font-medium hover:underline flex items-center gap-1">
                    {language === 'en' ? 'All Events' : 'Tutti gli Eventi'} <ArrowRight className="w-4 h-4" />
                  </Link>
                </div>

                {/* RISTORANTI */}
                <div className="bg-white rounded-2xl shadow-lg p-6 border border-gray-100">
                  <div className="flex items-center gap-3 mb-4">
                    <div className="w-10 h-10 bg-orange-100 rounded-lg flex items-center justify-center">
                      <Utensils className="w-5 h-5 text-orange-600" />
                    </div>
                    <h3 className="font-cinzel text-lg text-[#1B4332]">
                      {language === 'en' ? 'Restaurants' : 'Ristoranti'}
                    </h3>
                  </div>
                  {oggiPaestum?.ristoranti?.length > 0 ? (
                    <div className="space-y-3">
                      {oggiPaestum.ristoranti.slice(0, 3).map((ristorante, idx) => (
                        <button 
                          key={idx}
                          onClick={() => setSelectedPlace({ ...ristorante, tipo: 'ristorante' })}
                          className="w-full p-3 bg-gray-50 rounded-lg hover:bg-orange-50 hover:border-orange-200 border border-transparent transition-colors text-left"
                        >
                          <div className="font-medium text-sm text-[#1B4332]">{ristorante.nome}</div>
                          <div className="text-xs text-gray-500">{ristorante.tipo}</div>
                        </button>
                      ))}
                    </div>
                  ) : (
                    <p className="text-gray-400 text-sm">{language === 'en' ? 'Coming soon' : 'Presto disponibili'}</p>
                  )}
                  <Link to="/structures?filter=ristorante" className="mt-4 text-[#C5A059] text-sm font-medium hover:underline flex items-center gap-1">
                    {language === 'en' ? 'All Restaurants' : 'Tutti i Ristoranti'} <ArrowRight className="w-4 h-4" />
                  </Link>
                </div>

                {/* SPIAGGE */}
                <div className="bg-white rounded-2xl shadow-lg p-6 border border-gray-100">
                  <div className="flex items-center gap-3 mb-4">
                    <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
                      <Waves className="w-5 h-5 text-blue-600" />
                    </div>
                    <h3 className="font-cinzel text-lg text-[#1B4332]">
                      {language === 'en' ? 'Beaches' : 'Spiagge'}
                    </h3>
                  </div>
                  {oggiPaestum?.spiagge?.length > 0 ? (
                    <div className="space-y-3">
                      {oggiPaestum.spiagge.slice(0, 3).map((spiaggia, idx) => (
                        <button 
                          key={idx} 
                          onClick={() => setSelectedPlace({ ...spiaggia, tipo: 'spiaggia' })}
                          className="w-full p-3 bg-gray-50 rounded-lg hover:bg-blue-50 hover:border-blue-200 border border-transparent transition-colors text-left"
                        >
                          <div className="font-medium text-sm text-[#1B4332]">{spiaggia.nome}</div>
                          <div className="text-xs text-gray-500 line-clamp-2">{spiaggia.descrizione}</div>
                        </button>
                      ))}
                    </div>
                  ) : (
                    <p className="text-gray-400 text-sm">{language === 'en' ? 'Loading...' : 'Caricamento...'}</p>
                  )}
                  <Link to="/structures?filter=spiaggia" className="mt-4 text-[#C5A059] text-sm font-medium hover:underline flex items-center gap-1">
                    {language === 'en' ? 'All Beaches' : 'Tutte le Spiagge'} <ArrowRight className="w-4 h-4" />
                  </Link>
                </div>

                {/* ESPERIENZE */}
                <div className="bg-white rounded-2xl shadow-lg p-6 border border-gray-100 md:col-span-2 lg:col-span-1">
                  <div className="flex items-center gap-3 mb-4">
                    <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center">
                      <Compass className="w-5 h-5 text-green-600" />
                    </div>
                    <h3 className="font-cinzel text-lg text-[#1B4332]">
                      {language === 'en' ? 'Experiences' : 'Esperienze'}
                    </h3>
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    {(oggiPaestum?.esperienze || [
                      { nome: 'Visita Templi', icona: '🏛️' },
                      { nome: 'Mozzarella Tour', icona: '🧀' },
                      { nome: 'Tour in Barca', icona: '⛵' },
                      { nome: 'Trekking', icona: '🥾' }
                    ]).slice(0, 4).map((esp, idx) => (
                      <div key={idx} className="p-3 bg-gray-50 rounded-lg text-center hover:bg-gray-100 transition-colors cursor-pointer">
                        <span className="text-2xl block mb-1">{esp.icona}</span>
                        <span className="text-xs font-medium text-[#1B4332]">{esp.nome}</span>
                      </div>
                    ))}
                  </div>
                  <Link to="/services" className="mt-4 text-[#C5A059] text-sm font-medium hover:underline flex items-center gap-1">
                    {language === 'en' ? 'All Experiences' : 'Tutte le Esperienze'} <ArrowRight className="w-4 h-4" />
                  </Link>
                </div>

                {/* OFFERTE */}
                {oggiPaestum?.offerte?.length > 0 && (
                  <div className="bg-gradient-to-br from-[#C5A059] to-[#D4AF6A] rounded-2xl shadow-lg p-6 text-white lg:col-span-2">
                    <div className="flex items-center gap-3 mb-4">
                      <div className="w-10 h-10 bg-white/20 rounded-lg flex items-center justify-center">
                        <Gift className="w-5 h-5" />
                      </div>
                      <h3 className="font-cinzel text-lg">
                        {language === 'en' ? 'Special Offers' : 'Offerte Speciali'}
                      </h3>
                    </div>
                    <div className="grid md:grid-cols-2 gap-3">
                      {oggiPaestum.offerte.slice(0, 2).map((offerta, idx) => (
                        <div key={idx} className="p-4 bg-white/10 rounded-lg backdrop-blur-sm">
                          <div className="font-medium">{offerta.titolo}</div>
                          <div className="text-sm text-white/80">{offerta.descrizione}</div>
                          {offerta.codice && (
                            <div className="mt-2 bg-white/20 px-3 py-1 rounded inline-block text-sm font-mono">
                              {offerta.codice}
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>

              {/* AI Chat Button (Mobile FAB) - hide when modal open */}
              {!selectedPlace && !showAiChat && (
                <button
                  onClick={() => setShowAiChat(true)}
                  className="fixed bottom-24 left-4 md:bottom-8 md:left-8 w-16 h-16 bg-gradient-to-br from-[#C5A059] to-[#D4AF61] text-white rounded-2xl shadow-2xl flex items-center justify-center z-50 hover:scale-110 hover:shadow-[#C5A059]/50 transition-all duration-300 animate-pulse-subtle"
                  data-testid="ai-chat-fab"
                  style={{ boxShadow: '0 8px 32px rgba(197, 160, 89, 0.4)' }}
                >
                  <Sparkles className="w-7 h-7" />
                </button>
              )}
            </>
          )}
        </div>
      </section>

      {/* AI Chat Modal - ORO THEME */}
      {showAiChat && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-end md:items-center justify-center">
          <div 
            className="bg-white w-full max-w-lg md:rounded-2xl rounded-t-2xl flex flex-col shadow-2xl"
            style={{ maxHeight: '75vh', paddingBottom: 'max(env(safe-area-inset-bottom), 80px)' }}
          >
            {/* Header - ORO ELEGANTE */}
            <div className="p-4 border-b flex items-center justify-between bg-gradient-to-r from-[#C5A059] to-[#D4AF61] text-white rounded-t-2xl">
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 bg-white/20 backdrop-blur-sm rounded-xl flex items-center justify-center">
                  <Sparkles className="w-6 h-6" />
                </div>
                <div>
                  <h3 className="font-cinzel text-lg font-semibold">{language === 'en' ? 'AI Concierge' : 'Concierge AI'}</h3>
                  <p className="text-xs text-white/80">{language === 'en' ? 'Your personal guide to Paestum' : 'La tua guida personale a Paestum'}</p>
                </div>
              </div>
              <button onClick={() => setShowAiChat(false)} className="p-2 hover:bg-white/20 rounded-xl transition-colors">
                <X className="w-6 h-6" />
              </button>
            </div>
            
            {/* Messages */}
            <div className="flex-1 overflow-y-auto p-4 space-y-4 min-h-[300px] bg-gray-50">
              {aiMessages.length === 0 && (
                <div className="text-center py-8">
                  <div className="w-20 h-20 bg-gradient-to-br from-[#C5A059] to-[#D4AF61] rounded-2xl flex items-center justify-center mx-auto mb-4 shadow-lg">
                    <Sparkles className="w-10 h-10 text-white" />
                  </div>
                  <h4 className="text-lg font-semibold text-gray-800 mb-2">{language === 'en' ? 'Hello! I\'m your AI Concierge' : 'Ciao! Sono il tuo Concierge AI'}</h4>
                  <p className="text-gray-600 mb-6 text-sm">{language === 'en' ? 'I know everything about La Maisonette and Paestum. Ask me anything!' : 'Conosco tutto su La Maisonette e Paestum. Chiedimi qualsiasi cosa!'}</p>
                  <div className="flex flex-wrap gap-2 justify-center">
                    {[
                      { text: language === 'en' ? 'What to do today?' : 'Cosa fare oggi?', icon: '🌞' },
                      { text: language === 'en' ? 'Best restaurants?' : 'Migliori ristoranti?', icon: '🍽️' },
                      { text: language === 'en' ? 'Beach tips' : 'Spiagge consigliate', icon: '🏖️' },
                      { text: language === 'en' ? 'Temple visit' : 'Visita ai Templi', icon: '🏛️' }
                    ].map((suggestion, idx) => (
                      <button
                        key={idx}
                        onClick={() => { setAiInput(suggestion.text); }}
                        className="px-4 py-2 bg-white border border-[#C5A059]/30 rounded-xl text-sm text-gray-700 hover:bg-[#C5A059]/10 hover:border-[#C5A059] transition-all flex items-center gap-2 shadow-sm"
                      >
                        <span>{suggestion.icon}</span>
                        {suggestion.text}
                      </button>
                    ))}
                  </div>
                </div>
              )}
              {aiMessages.map((msg, idx) => (
                <div key={idx} className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}>
                  <div className={`max-w-[85%] p-4 rounded-2xl shadow-sm ${
                    msg.role === 'user' 
                      ? 'bg-gradient-to-r from-[#C5A059] to-[#D4AF61] text-white rounded-br-md' 
                      : 'bg-white text-gray-800 rounded-bl-md border border-gray-100'
                  }`}>
                    {msg.content}
                  </div>
                </div>
              ))}
              {aiLoading && (
                <div className="flex justify-start">
                  <div className="bg-white p-4 rounded-2xl rounded-bl-md shadow-sm border border-gray-100">
                    <div className="flex gap-1.5">
                      <div className="w-2.5 h-2.5 bg-[#C5A059] rounded-full animate-bounce" style={{ animationDelay: '0ms' }} />
                      <div className="w-2.5 h-2.5 bg-[#C5A059] rounded-full animate-bounce" style={{ animationDelay: '150ms' }} />
                      <div className="w-2.5 h-2.5 bg-[#C5A059] rounded-full animate-bounce" style={{ animationDelay: '300ms' }} />
                    </div>
                  </div>
                </div>
              )}
            </div>
            
            {/* Input - ORO ACCENTS */}
            <div className="p-4 border-t bg-white flex gap-3">
              <input
                type="text"
                value={aiInput}
                onChange={(e) => setAiInput(e.target.value)}
                onKeyPress={(e) => e.key === 'Enter' && sendAiMessage()}
                placeholder={language === 'en' ? 'Ask me anything...' : 'Chiedimi qualcosa...'}
                className="flex-1 px-5 py-3 border-2 border-gray-200 rounded-xl focus:outline-none focus:border-[#C5A059] focus:ring-2 focus:ring-[#C5A059]/20 transition-all"
              />
              <button
                onClick={sendAiMessage}
                disabled={aiLoading || !aiInput.trim()}
                className="w-12 h-12 bg-gradient-to-r from-[#C5A059] to-[#D4AF61] text-white rounded-xl flex items-center justify-center hover:shadow-lg hover:shadow-[#C5A059]/30 transition-all disabled:opacity-50 disabled:hover:shadow-none"
              >
                <Send className="w-5 h-5" />
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Place Details Modal */}
      {selectedPlace && (
        <div className="fixed inset-0 bg-black/60 z-50 flex items-end justify-center" onClick={() => setSelectedPlace(null)}>
          <div 
            className="bg-white w-full max-w-lg rounded-t-2xl max-h-[75vh] overflow-y-auto pb-safe"
            style={{ paddingBottom: 'max(env(safe-area-inset-bottom), 100px)' }}
            onClick={(e) => e.stopPropagation()}
          >
            {/* Image Header */}
            {selectedPlace.immagine && (
              <div className="h-48 relative">
                <img 
                  src={selectedPlace.immagine} 
                  alt={selectedPlace.nome}
                  className="w-full h-full object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent" />
                <button 
                  onClick={() => setSelectedPlace(null)}
                  className="absolute top-4 right-4 w-8 h-8 bg-white/90 rounded-full flex items-center justify-center"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>
            )}
            
            {/* Content */}
            <div className="p-6">
              {!selectedPlace.immagine && (
                <button 
                  onClick={() => setSelectedPlace(null)}
                  className="absolute top-4 right-4 w-8 h-8 bg-gray-100 rounded-full flex items-center justify-center"
                >
                  <X className="w-4 h-4" />
                </button>
              )}
              
              {/* Type Badge */}
              <div className="flex items-center gap-2 mb-3">
                <span className={`px-3 py-1 rounded-full text-xs font-medium ${
                  selectedPlace.tipo === 'spiaggia' ? 'bg-blue-100 text-blue-700' :
                  selectedPlace.tipo === 'ristorante' ? 'bg-orange-100 text-orange-700' :
                  selectedPlace.tipo === 'evento' ? 'bg-purple-100 text-purple-700' :
                  'bg-gray-100 text-gray-700'
                }`}>
                  {selectedPlace.tipo === 'spiaggia' ? '🏖️ Spiaggia' :
                   selectedPlace.tipo === 'ristorante' ? '🍽️ Ristorante' :
                   selectedPlace.tipo === 'evento' ? '📅 Evento' :
                   selectedPlace.tipo}
                </span>
                {selectedPlace.data && (
                  <span className="text-sm text-gray-500">
                    {new Date(selectedPlace.data).toLocaleDateString('it-IT', { weekday: 'long', day: 'numeric', month: 'long' })}
                  </span>
                )}
              </div>
              
              <h3 className="font-cinzel text-2xl text-[#1B4332] mb-3">{selectedPlace.nome}</h3>
              
              {/* Location */}
              {(selectedPlace.luogo || selectedPlace.indirizzo) && (
                <div className="flex items-center gap-2 text-gray-600 mb-4">
                  <MapPin className="w-4 h-4 text-[#C5A059]" />
                  <span className="text-sm">{selectedPlace.luogo || selectedPlace.indirizzo}</span>
                </div>
              )}
              
              {/* Description */}
              {selectedPlace.descrizione && (
                <p className="text-gray-600 text-sm leading-relaxed mb-6">
                  {selectedPlace.descrizione}
                </p>
              )}
              
              {/* Contact Info */}
              {selectedPlace.telefono && (
                <a 
                  href={`tel:${selectedPlace.telefono}`}
                  className="flex items-center gap-2 text-[#1B4332] mb-4 hover:text-[#C5A059] transition-colors"
                >
                  <span className="text-lg">📞</span>
                  <span>{selectedPlace.telefono}</span>
                </a>
              )}
              
              {/* Actions */}
              <div className="flex gap-3 mt-6">
                {selectedPlace.tipo === 'evento' && selectedPlace.link && (
                  <a 
                    href={selectedPlace.link}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex-1 bg-[#1B4332] text-white py-3 rounded-xl text-center font-medium hover:bg-[#2D5A45] transition-colors"
                  >
                    {language === 'en' ? 'More Info' : 'Maggiori Info'}
                  </a>
                )}
                
                {selectedPlace.lat && selectedPlace.lng && (
                  <a 
                    href={`https://maps.google.com/?q=${selectedPlace.lat},${selectedPlace.lng}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex-1 bg-[#C5A059] text-white py-3 rounded-xl text-center font-medium hover:bg-[#D4AF6A] transition-colors flex items-center justify-center gap-2"
                  >
                    <Navigation className="w-4 h-4" />
                    {language === 'en' ? 'Directions' : 'Indicazioni'}
                  </a>
                )}
                
                {!selectedPlace.link && !selectedPlace.lat && (
                  <button 
                    onClick={() => {
                      setSelectedPlace(null);
                      setShowAiChat(true);
                      setAiInput(`Dimmi di più su ${selectedPlace.nome}`);
                    }}
                    className="flex-1 bg-[#1B4332] text-white py-3 rounded-xl text-center font-medium hover:bg-[#2D5A45] transition-colors flex items-center justify-center gap-2"
                  >
                    <MessageCircle className="w-4 h-4" />
                    {language === 'en' ? 'Ask AI' : 'Chiedi all\'AI'}
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Timeline di Paestum - Storia Millenaria */}
      <section className="py-16 px-4 bg-gradient-to-b from-[#1A202C] to-[#2D3748] relative overflow-hidden">
        {/* Decorative background */}
        <div className="absolute inset-0 opacity-5">
          <div className="absolute top-10 left-10 w-40 h-40 border border-white/20 rounded-full" />
          <div className="absolute bottom-10 right-10 w-60 h-60 border border-white/10 rounded-full" />
          <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-80 h-80 border border-white/5 rounded-full" />
        </div>

        <div className="max-w-6xl mx-auto relative z-10">
          <div className="text-center mb-12">
            <p className="font-cormorant text-[#C5A059] tracking-widest uppercase text-sm mb-3">
              {language === 'en' ? 'Millennial History' : 'Storia Millenaria'}
            </p>
            <h2 className="font-cinzel text-3xl md:text-4xl text-white mb-4">
              {language === 'en' ? 'The Legend of Paestum' : 'La Leggenda di Paestum'}
            </h2>
            <div className="w-24 h-1 bg-gradient-to-r from-transparent via-[#C5A059] to-transparent mx-auto" />
          </div>

          {/* Timeline */}
          <div className="relative">
            {/* Central line */}
            <div className="absolute left-1/2 top-0 bottom-0 w-px bg-gradient-to-b from-[#C5A059]/50 via-[#C5A059] to-[#C5A059]/50 hidden md:block" />
            
            <div className="space-y-8 md:space-y-0">
              {paestumTimeline.map((item, index) => (
                <div 
                  key={item.year}
                  className={`relative md:flex items-center ${index % 2 === 0 ? 'md:flex-row' : 'md:flex-row-reverse'}`}
                >
                  {/* Content */}
                  <div className={`md:w-1/2 ${index % 2 === 0 ? 'md:pr-12 md:text-right' : 'md:pl-12'}`}>
                    <div 
                      className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-2xl p-6 hover:bg-white/10 transition-all duration-500 hover:scale-105 cursor-default group"
                    >
                      <span className="inline-block px-3 py-1 bg-[#C5A059] text-white text-sm font-bold rounded-full mb-3">
                        {item.year}
                      </span>
                      <h3 className="font-cinzel text-xl text-white mb-2 group-hover:text-[#C5A059] transition-colors">
                        {language === 'en' ? item.titleEn : item.title}
                      </h3>
                      <p className="text-gray-400 text-sm">
                        {language === 'en' ? item.descEn : item.desc}
                      </p>
                    </div>
                  </div>
                  
                  {/* Center dot */}
                  <div className="hidden md:flex absolute left-1/2 -translate-x-1/2 w-4 h-4 bg-[#C5A059] rounded-full border-4 border-[#1A202C] z-10" />
                  
                  {/* Spacer for opposite side */}
                  <div className="hidden md:block md:w-1/2" />
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Quick Actions */}
      <section className="py-12 px-4 bg-[#F9F9F7]">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {/* Check-in / Dashboard - depends on status */}
            {isAuthenticated && hasActiveCheckin ? (
              <Link 
                to="/dashboard" 
                className="bg-white p-6 rounded-lg shadow-soft hover:shadow-lg transition-shadow text-center group"
              >
                <div className="w-14 h-14 bg-[#C5A059]/10 rounded-full flex items-center justify-center mx-auto mb-3 group-hover:bg-[#C5A059]/20 transition-colors">
                  <LayoutDashboard className="w-7 h-7 text-[#C5A059]" />
                </div>
                <h3 className="font-cinzel text-[#1A202C]">Dashboard</h3>
                <p className="text-xs text-[#4A5568] mt-1">Il tuo soggiorno</p>
              </Link>
            ) : (
              <Link 
                to={isAuthenticated ? "/checkin" : "/login"} 
                className="bg-white p-6 rounded-lg shadow-soft hover:shadow-lg transition-shadow text-center group"
              >
                <div className="w-14 h-14 bg-[#C5A059]/10 rounded-full flex items-center justify-center mx-auto mb-3 group-hover:bg-[#C5A059]/20 transition-colors">
                  <ClipboardCheck className="w-7 h-7 text-[#C5A059]" />
                </div>
                <h3 className="font-cinzel text-[#1A202C]">Check-in</h3>
                <p className="text-xs text-[#4A5568] mt-1">Online</p>
              </Link>
            )}

            {/* Prenota - Always visible */}
            <Link 
              to="/booking" 
              className="bg-white p-6 rounded-lg shadow-soft hover:shadow-lg transition-shadow text-center group"
            >
              <div className="w-14 h-14 bg-[#C5A059]/10 rounded-full flex items-center justify-center mx-auto mb-3 group-hover:bg-[#C5A059]/20 transition-colors">
                <CalendarCheck className="w-7 h-7 text-[#C5A059]" />
              </div>
              <h3 className="font-cinzel text-[#1A202C]">Prenota</h3>
              <p className="text-xs text-[#4A5568] mt-1">Verifica disponibilità</p>
            </Link>

            {/* Only visible if authenticated */}
            {isAuthenticated ? (
              <>
                <Link 
                  to="/services" 
                  className="bg-white p-6 rounded-lg shadow-soft hover:shadow-lg transition-shadow text-center group"
                >
                  <div className="w-14 h-14 bg-[#C5A059]/10 rounded-full flex items-center justify-center mx-auto mb-3 group-hover:bg-[#C5A059]/20 transition-colors">
                    <Sparkles className="w-7 h-7 text-[#C5A059]" />
                  </div>
                  <h3 className="font-cinzel text-[#1A202C]">Servizi</h3>
                  <p className="text-xs text-[#4A5568] mt-1">Richiedi servizi</p>
                </Link>

                <Link 
                  to="/loyalty" 
                  className="bg-white p-6 rounded-lg shadow-soft hover:shadow-lg transition-shadow text-center group"
                >
                  <div className="w-14 h-14 bg-[#C5A059]/10 rounded-full flex items-center justify-center mx-auto mb-3 group-hover:bg-[#C5A059]/20 transition-colors">
                    <Gift className="w-7 h-7 text-[#C5A059]" />
                  </div>
                  <h3 className="font-cinzel text-[#1A202C]">Fedeltà</h3>
                  <p className="text-xs text-[#4A5568] mt-1">Punti e Premi</p>
                </Link>
              </>
            ) : (
              <>
                <Link 
                  to="/events" 
                  className="bg-white p-6 rounded-lg shadow-soft hover:shadow-lg transition-shadow text-center group"
                >
                  <div className="w-14 h-14 bg-[#C5A059]/10 rounded-full flex items-center justify-center mx-auto mb-3 group-hover:bg-[#C5A059]/20 transition-colors">
                    <Calendar className="w-7 h-7 text-[#C5A059]" />
                  </div>
                  <h3 className="font-cinzel text-[#1A202C]">Eventi</h3>
                  <p className="text-xs text-[#4A5568] mt-1">Scopri cosa fare</p>
                </Link>

                <Link 
                  to="/loyalty" 
                  className="bg-white p-6 rounded-lg shadow-soft hover:shadow-lg transition-shadow text-center group"
                >
                  <div className="w-14 h-14 bg-[#C5A059]/10 rounded-full flex items-center justify-center mx-auto mb-3 group-hover:bg-[#C5A059]/20 transition-colors">
                    <Gift className="w-7 h-7 text-[#C5A059]" />
                  </div>
                  <h3 className="font-cinzel text-[#1A202C]">Fedeltà</h3>
                  <p className="text-xs text-[#4A5568] mt-1">Il programma</p>
                </Link>
              </>
            )}
          </div>
        </div>
      </section>

      {/* House Rules Section - Solo link */}
      <section className="py-12 px-4 bg-white">
        <div className="max-w-7xl mx-auto">
          <div className="text-center">
            <Link to="/house-rules" className="inline-block group">
              <div className="bg-[#F9F9F7] hover:bg-[#1A202C] transition-all duration-300 px-8 py-6 rounded-lg border border-[#C5A059]/30 hover:border-[#C5A059]">
                <div className="flex items-center justify-center gap-4">
                  <span className="text-3xl">📋</span>
                  <div className="text-left">
                    <p className="font-cormorant text-[#C5A059] tracking-[0.15em] uppercase text-xs mb-1">
                      Informazioni Utili
                    </p>
                    <h3 className="font-cinzel text-xl text-[#1A202C] group-hover:text-white transition-colors duration-300">
                      Regole della Casa
                    </h3>
                  </div>
                  <ArrowRight className="w-5 h-5 text-[#C5A059] ml-4 group-hover:translate-x-1 transition-transform duration-300" />
                </div>
              </div>
            </Link>
          </div>
        </div>
      </section>

      {/* Events Section - Always visible */}
      <section className="py-12 md:py-16 px-4 bg-[#1A202C]">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-8 md:mb-10">
            <p className="font-cormorant text-[#C5A059] tracking-[0.2em] uppercase text-sm mb-3">
              Non Perderti
            </p>
            <h2 className="font-cinzel text-2xl md:text-3xl text-white mb-4">
              Eventi in Zona
            </h2>
            <div className="w-20 h-1 bg-[#C5A059] mx-auto" />
          </div>

          <div className="grid md:grid-cols-3 gap-4 md:gap-6">
            {events.length > 0 ? events.map((event, index) => {
              // Format date range
              const formatEventDate = () => {
                const startDate = new Date(event.data).toLocaleDateString('it-IT', { day: 'numeric', month: 'short' });
                if (event.data_fine && event.data_fine !== event.data) {
                  const endDate = new Date(event.data_fine).toLocaleDateString('it-IT', { day: 'numeric', month: 'short' });
                  return `${startDate} - ${endDate}`;
                }
                return startDate;
              };
              
              // Get image URL with fallback
              const getEventImageUrl = (url) => {
                if (!url) return null;
                if (url.startsWith('http')) return url;
                if (url.startsWith('/api')) {
                  const baseUrl = process.env.REACT_APP_BACKEND_URL || '';
                  return `${baseUrl}${url}`;
                }
                return url;
              };
              
              const imageUrl = getEventImageUrl(event.immagine_url);
              
              return (
                <Link 
                  to="/events"
                  key={event.id || index} 
                  className="bg-white/10 backdrop-blur-sm rounded-xl overflow-hidden group hover:bg-white/15 transition-all duration-300 hover:scale-[1.02]"
                >
                  <div className="h-36 md:h-44 overflow-hidden relative">
                    {imageUrl ? (
                      <img 
                        src={imageUrl} 
                        alt={event.titolo}
                        className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                        onError={(e) => {
                          e.target.onerror = null;
                          e.target.src = 'https://images.unsplash.com/photo-1504644708628-9c1dd99f497f?auto=format&fit=crop&q=80&w=800';
                        }}
                      />
                    ) : (
                      <div className="w-full h-full bg-gradient-to-br from-[#C5A059]/30 to-[#1A202C] flex items-center justify-center">
                        <Calendar className="w-12 h-12 text-[#C5A059]/50" />
                      </div>
                    )}
                    {/* Gradient overlay */}
                    <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent" />
                    {/* Date badge on image */}
                    <div className="absolute bottom-3 left-3 bg-[#C5A059] text-white px-3 py-1 rounded-full text-xs font-semibold">
                      {formatEventDate()}
                    </div>
                  </div>
                  <div className="p-4">
                    <h3 className="font-cinzel text-base md:text-lg text-white mb-1 line-clamp-1">{event.titolo}</h3>
                    <p className="font-manrope text-xs md:text-sm text-gray-300 line-clamp-2 mb-2">{event.descrizione}</p>
                    <div className="flex items-center gap-1 text-[#C5A059] text-xs">
                      <MapPin className="w-3 h-3" />
                      <span className="line-clamp-1">{event.luogo}</span>
                    </div>
                  </div>
                </Link>
              );
            }) : (
              <div className="col-span-3 text-center py-8">
                <p className="text-gray-400">Nessun evento in programma</p>
              </div>
            )}
          </div>

          <div className="text-center mt-6 md:mt-8">
            <Link to="/events">
              <Button className="bg-[#C5A059] hover:bg-[#B08D45] text-white rounded-xl">
                Tutti gli Eventi
                <ArrowRight className="ml-2 w-4 h-4" />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* About Section with Bed Photo */}
      <section className="py-20 px-4 bg-white overflow-hidden">
        <div className="max-w-7xl mx-auto">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <p className="font-cormorant text-[#C5A059] tracking-[0.2em] uppercase text-sm mb-4">
                Le Nostre Casette
              </p>
              <h2 className="font-cinzel text-4xl text-[#1A202C] mb-6">
                Un Rifugio di Pace
              </h2>
              <p className="font-manrope text-[#4A5568] leading-relaxed mb-6">
                Le nostre due casette gemelle offrono il perfetto equilibrio tra tradizione e comfort. 
                Con soffitti in legno a vista, arredi curati nei minimi dettagli e un'atmosfera calda 
                e accogliente, ogni soggiorno diventa un'esperienza indimenticabile.
              </p>
              <ul className="space-y-3 mb-8">
                {['WiFi gratuito', 'Aria condizionata', 'Giardino privato', 'Parcheggio gratuito', 'Colazione inclusa'].map((item, i) => (
                  <li key={i} className="flex items-center gap-3 font-manrope text-[#4A5568]">
                    <div className="w-2 h-2 bg-[#C5A059] rounded-full" />
                    {item}
                  </li>
                ))}
              </ul>
              <Link to="/about">
                <Button className="bg-[#C5A059] hover:bg-[#B08D45] text-white">
                  Scopri la Struttura
                  <ArrowRight className="ml-2 w-4 h-4" />
                </Button>
              </Link>
            </div>
            <div className="relative">
              <img 
                src="https://customer-assets.emergentagent.com/job_88383793-9b8e-4520-b738-24dee3c24d4e/artifacts/x28xxl91_678938161.jpg"
                alt="Camera La Maisonette Paestum"
                className="w-full h-[500px] object-cover rounded-lg shadow-xl"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Loyalty Program CTA */}
      <section className="py-16 px-4 bg-gradient-to-br from-[#C5A059] to-[#B08D45]">
        <div className="max-w-4xl mx-auto text-center text-white">
          <Gift className="w-16 h-16 mx-auto mb-6 opacity-80" />
          <h2 className="font-cinzel text-3xl mb-4">
            Programma Fedeltà
          </h2>
          <p className="font-manrope text-lg mb-6 opacity-90">
            Ogni €10 spesi = 1 punto. Accumula punti e riscatta premi esclusivi!
          </p>
          <div className="flex flex-wrap justify-center gap-4 mb-8">
            {rewards.length > 0 ? (
              rewards.map((reward) => (
                <div key={reward.id} className="bg-white/20 backdrop-blur-sm px-4 py-2 rounded-full">
                  {reward.icona || '🎁'} {reward.nome}: {reward.punti_richiesti} punti
                </div>
              ))
            ) : (
              <>
                <div className="bg-white/20 backdrop-blur-sm px-4 py-2 rounded-full">
                  🍾 Spumante: 30 punti
                </div>
                <div className="bg-white/20 backdrop-blur-sm px-4 py-2 rounded-full">
                  🏛️ Ingressi Paestum: 50 punti
                </div>
                <div className="bg-white/20 backdrop-blur-sm px-4 py-2 rounded-full">
                  🏠 Soggiorno Gratis: 100 punti
                </div>
              </>
            )}
          </div>
          <Link to="/loyalty">
            <Button className="bg-white text-[#C5A059] hover:bg-gray-100 font-cinzel">
              Scopri Tutti i Premi
              <ArrowRight className="ml-2 w-4 h-4" />
            </Button>
          </Link>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 px-4 bg-[#1A202C] relative overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-0 right-0 w-96 h-96 bg-[#C5A059] rounded-full blur-3xl" />
          <div className="absolute bottom-0 left-0 w-96 h-96 bg-[#C5A059] rounded-full blur-3xl" />
        </div>
        <div className="max-w-4xl mx-auto text-center relative z-10">
          <p className="font-cormorant text-[#C5A059] tracking-[0.2em] uppercase text-sm mb-4">
            Inizia la Tua Esperienza
          </p>
          <h2 className="font-cinzel text-4xl md:text-5xl text-white mb-6">
            Prenota il Tuo Soggiorno
          </h2>
          <p className="font-manrope text-gray-400 mb-10 max-w-2xl mx-auto">
            Vivi un'esperienza unica tra storia, natura e comfort. 
            Le nostre casette ti aspettano per un soggiorno indimenticabile.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/booking">
              <Button className="bg-[#C5A059] hover:bg-[#B08D45] text-white font-cinzel tracking-widest uppercase px-8 py-6">
                Verifica Disponibilità
                <ArrowRight className="ml-2 w-5 h-5" />
              </Button>
            </Link>
          </div>
        </div>
      </section>
    </Layout>
  );
}
